import React, { useState, useEffect } from 'react';
import { ArrowBigRight, Menu, X } from 'lucide-react';
import Button from './Button';

const navItems = [
  { label: 'Home', href: '/' },
  { label: 'Our Approach', href: '/approach' },
  { label: 'Innovation Toolkit', href: '/tools' },
  { label: 'Resources', href: '/blog' },
  { label: 'Contact', href: '/contact' },
];

export default function HeaderNav() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // toggle header shadow on scroll
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // lock body scroll when mobile is open
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : 'auto';
  }, [isMenuOpen]);

  return (
    <>
      <header
        className={`
          fixed inset-x-0 top-0 z-50 transition-all duration-300
          ${scrolled
            ? 'bg-white/95 backdrop-blur-md shadow-md py-3'
            : 'bg-white/90 backdrop-blur-sm py-4 md:py-5 lg:py-6'}
        `}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12 flex items-center justify-between">
          {/* Logo */}
          <a href="/" className="flex items-center">
            <img 
              src="/img/logo.svg" 
              alt="Lusidea" 
              className="h-8 md:h-10"
              width="auto"
              height="40"
            />
          </a>

          {/* Desktop Links */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className="text-sm font-medium text-gray-700 hover:text-primary transition-colors"
              >
                {item.label}
              </a>
            ))}

            <Button href="/contact" variant="primary" size="sm" icon={ArrowBigRight}>
              Let's Talk
            </Button>
          </nav>

          {/* Mobile hamburger */}
          <button
            onClick={() => setIsMenuOpen((o) => !o)}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
            className="lg:hidden p-2 focus:outline-none"
          >
            {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </header>

      {/* Mobile backdrop */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40 lg:hidden"
          onClick={() => setIsMenuOpen(false)}
        />
      )}

      {/* Mobile drawer */}
      <div
        id="mobile-menu"
        className={`
          fixed inset-y-0 right-0 z-50 w-full max-w-xs bg-white
          transform transition-transform duration-300 ease-in-out lg:hidden
          ${isMenuOpen ? 'translate-x-0 shadow-xl' : 'translate-x-full'}
        `}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-full flex flex-col pt-20 pb-8 px-6 overflow-y-auto">
          {/* Navigation items only - no logo duplication */}
          <nav className="flex flex-col space-y-4">
            {navItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                onClick={() => setIsMenuOpen(false)}
                className="block py-2 text-lg font-medium text-gray-700 hover:text-primary transition-colors"
              >
                {item.label}
              </a>
            ))}
          </nav>
          <div className="mt-auto pt-8 border-t border-gray-200">
            <Button
              href="/contact"
              variant="primary"
              size="lg"
              fullWidth
              icon={ArrowBigRight}
              onClick={() => setIsMenuOpen(false)}
            >
              Let's Talk
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}